import { useState } from "react";
import PageLayout from "layouts/PageLayout";
import CalendarPicker from "components/CalendarPicker/CalendarPicker";
import CustomButton from "components/CustomButtons/Button.js";
import Table from "components/ReactTable/ReactTableV2";
import { makeStyles } from "@material-ui/core/styles";
import { Grid, Box, Button, Typography, Popover, Card, Divider, Tooltip } from "@material-ui/core";
import { DateRange as DateRangeIcon } from "components/MaterialSymbol/MaterialIcons";
import { ArrowDropUp as ArrowDropUpIcon } from "components/MaterialSymbol/MaterialIcons";
import ExportSalesDialogButton from "components/ExportSalesDialogButton/ExportSalesDialogButton";
import CountryToggleButtons from "components/CountryToggleButtons/CountryToggleButtons";
import useSalesReport from "hooks/useSalesReport";
import useOrderMarginReport from "hooks/useOrderMarginReport";
import usePriceFormatter from "hooks/usePriceFormatter";
import useDealer from "hooks/useDealer";
import PrintOrder from "components/PrintOrder/PrintOrder";
import {
    format,
    startOfMonth,
    endOfMonth,
    subMonths,
    startOfWeek,
    startOfYear,
    subYears,
    endOfYear,
} from "date-fns";

const useStyles = makeStyles({
    centerContent: {
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
    },
    customTitle: {
        fontSize: 12,
        fontWeight: "bold",
    },
    customDetail: {
        fontSize: 11,
    },
    greenText: {
        fontSize: 50,
        marginTop: 25,
        textAlign: "center",
        color: "#8ec63f",
        fontWeight: 500,
    },
    redText: {
        fontSize: 50,
        marginTop: 25,
        textAlign: "center",
        color: "#ed1c25",
        fontWeight: 500,
    },
    normalText: {
        fontSize: 50,
        fontSize: 50,
        marginTop: 25,
        textAlign: "center",
        fontWeight: 500,
    },
    textCenter: {
        textAlign: "center",
    },
});

const filterOptions = [
    { title: "Last month", option: "last_month" },
    { title: "This month", option: "this_month" },
    { title: "This week", option: "this_week" },
    { title: "YTD report", option: "ytd_report" },
    { title: "Last year", option: "last_year" },
];

const getDateRangeForOption = (option) => {
    const now = new Date();
    let start, end;

    switch (option) {
        case "last_month": {
            const lastMonth = subMonths(now, 1);
            start = startOfMonth(lastMonth);
            end = endOfMonth(lastMonth);
            break;
        }
        case "this_month":
            start = startOfMonth(now);
            end = now;
            break;
        case "this_week":
            start = startOfWeek(now, { weekStartsOn: 1 }); // Monday
            end = now;
            break;
        case "ytd_report":
            start = startOfYear(now);
            end = now;
            break;
        case "last_year": {
            const lastYear = subYears(now, 1);
            start = startOfYear(lastYear);
            end = endOfYear(lastYear);
            break;
        }
        default:
            return "";
    }

    return `${format(start, "yyyy-MM-dd")} - ${format(end, "yyyy-MM-dd")}`;
};

const currencyKeys = [
    "grossSiteSalesGenerated",
    "netSales",
    "netSalesShipped",
    "salesCanceledLost",
    "salesPendingOnOrder",
    "totalSales",
    "averageSalesPerDay",
    "averageSale",
    "averageRetainedMargin",
    "collectedShipping",
    "totalTaxCollected",
    "totalNetProfits",
    "averageProfitPerSale",
    "totalLostProfits",
    "dealerProfits",
    "retainedDealerMargin",
    "commissionsPaid",
    "retainedCommMargin",
    "netSalesAmount",
    "accessory",
    "replacement",
    "other",
];

const percentageKeys = [
    "fulfillmentRatio",
    "conversion",
    "taxRate",
    "percentOfNetProfit",
    "netSalesPercentage",
    "visitsToCart",
    "visitsToOrder",
    "visitsToMessage",
    "visitsToCalls",
];

const OptionsList = ({ title = "", options = [], selectedCountry = "USA" }) => {
    const classes = useStyles();
    const { formatPriceByRegion } = usePriceFormatter();

    const formatValue = (option) => {
        const val = Number(option.value) || 0;
        if (currencyKeys.includes(option.key)) {
            return formatPriceByRegion(selectedCountry, val);
        }
        if (percentageKeys.includes(option.key)) {
            return `${val.toFixed(2)}%`;
        }
        return val.toLocaleString("en-US", {
            minimumFractionDigits: 0,
            maximumFractionDigits: 2,
        });
    };

    return (
        <Box flex={1}>
            <Box p={2}>
                <Typography variant="h6" className={classes.customTitle}>
                    {title}:
                </Typography>
                {options.map((option, index) => (
                    <Box key={index} display="flex" mt={index === 0 ? 2 : 0} mb={2}>
                        <Typography className={classes.customDetail} style={{ flexGrow: 1 }}>
                            {option.label}
                        </Typography>
                        <Typography className={classes.customDetail}>
                            {formatValue(option)}
                        </Typography>
                    </Box>
                ))}
            </Box>
        </Box>
    );
};

export default function SalesReportDetail() {
    const classes = useStyles();
    const [anchorEl, setAnchorEl] = useState(null);
    const { formatPriceByRegion } = usePriceFormatter();
    const dealer = useDealer();

    const {
        selectedOption,
        setSelectedOption,
        customDateRange,
        setCustomDateRange,
        salesReportData,
        handleFilterByDateRangeClick,
        dealerId,
        dealers,
        selectedCountry,
        setSelectedCountry,
    } = useSalesReport();

    const {
        totalOrders = 0,
        ordersCanceled = 0,
        fulfillmentRatio = 0,
        netSalesPercentage = 0,
        netSalesAmount = 0,
        engagements = [],
        closingRatio = [],
        orderPerformance = [],
        salesPerformance = [],
        shippingPerformance = [],
        catalogPerformance = [],
        tax = [],
        profits = [],
    } = salesReportData;

    const order = useOrderMarginReport(
        customDateRange.startDate,
        customDateRange.endDate,
        selectedCountry,
    );

    const summaryRegion =
        dealerId === 0 ? selectedCountry : dealer.country || dealer.currencySymbol || selectedCountry;

    const handleClick = (event) => {
        setAnchorEl(event.currentTarget);
    };

    const handleClose = () => {
        setAnchorEl(null);
    };

    const open = Boolean(anchorEl);
    const id = open ? "popover-textfields" : undefined;

    const columns = [
        {
            Header: <Box textAlign="center">#</Box>,
            id: "rank",
            disableSortBy: true,
            Cell: ({ row }) => (
                <Box textAlign="center" fontWeight="bold">
                    {Math.max(0, (order.pageIndex || 1) - 1) * order.pageSize + row.index + 1}
                </Box>
            ),
        },
        {
            Header: "Order Date",
            accessor: "orderDate",
            Cell: ({ value }) => {
                const date = new Date(value);
                const formatted = format(date, "yyyy-MM-dd HH:mm");
                return <>{formatted}</>;
            },
        },
        {
            Header: "Order Ref#",
            accessor: "orderRef",
            Cell: ({ row: { original } }) => (
                <Box display="flex" flexDirection="column" alignItems="center">
                    <Typography variant="body1" component="div">
                        #{original.orderNumber}{" "}
                        <PrintOrder dealerId={original.dealerId} id={original.orderId} />
                    </Typography>
                    <Typography color="textSecondary" style={{ fontSize: 13 }}>
                        {original.billCompany}
                    </Typography>
                </Box>
            ),
        },
        {
            Header: "Invoice Number",
            accessor: "orderNumber",
        },
        {
            Header: "URL Origin",
            accessor: "company",
        },
        {
            Header: "Status",
            accessor: "orderStatus",
        },
        {
            Header: "Sale Price",
            accessor: (row) => formatPriceByRegion(row.billCountry, row.salePrice),
            id: "salePrice",
        },
        {
            Header: "Charged Amount",
            accessor: (row) => formatPriceByRegion(row.billCountry, row.chargedAmount),
            id: "chargedAmount",
        },
        {
            Header: "Net Sale",
            accessor: (row) => formatPriceByRegion(row.billCountry, row.netSale),
            id: "netSale",
        },
        {
            Header: "Items",
            accessor: (row) => row.items ?? 0,
            id: "items",
        },
        {
            Header: "Order Margin",
            accessor: (row) =>
                row.orderMarginPercent != null
                    ? `${Number(row.orderMarginPercent).toFixed(2)}%`
                    : "0%",
            id: "orderMarginPercent",
        },
        {
            Header: "Gross Profits",
            accessor: (row) => formatPriceByRegion(row.billCountry, row.grossProfits),
            id: "grossProfits",
        },
        /*{
            Header: "Revenue Share",
            accessor: "revenueShare",
        },*/
    ];

    return (
        <PageLayout showAllDealers={true}>
            <Box display="flex" alignItems="center">
                <Box flexGrow={1}>Dealer Order Report</Box>
                <Box>
                    <Grid container spacing={1}>
                        {dealerId === 0 && (
                            <>
                                <Grid item>
                                    <CountryToggleButtons
                                        selectedCountry={selectedCountry}
                                        setSelectedCountry={setSelectedCountry}
                                        onChange={(newCountry) => {
                                            setSelectedCountry(newCountry);
                                        }}
                                    />
                                </Grid>

                                <Grid item>
                                    <Divider
                                        orientation="vertical"
                                        flexItem
                                        style={{
                                            height: 30,
                                            alignSelf: "center",
                                            backgroundColor: "#BDBDBD",
                                            margin: "0 8px",
                                        }}
                                    />
                                </Grid>
                            </>
                        )}

                        {filterOptions.map((filter, index) => (
                            <Grid key={index} item>
                                <Tooltip title={getDateRangeForOption(filter.option)} placement="top" arrow>
                                    <Button
                                        variant={
                                            filter.option === selectedOption ? "contained" : "outlined"
                                        }
                                        color="primary"
                                        size="small"
                                        onClick={() => {
                                            setSelectedOption(filter.option);
                                            order.setPageIndex(1);
                                        }}
                                    >
                                        {filter.title}
                                    </Button>
                                </Tooltip>
                            </Grid>
                        ))}

                        <Grid item>
                            <Button
                                onClick={handleClick}
                                variant="contained"
                                size="small"
                                startIcon={<DateRangeIcon />}
                            >
                                Custom
                            </Button>
                            <Popover
                                id={id}
                                open={open}
                                anchorEl={anchorEl}
                                onClose={handleClose}
                                anchorOrigin={{
                                    vertical: "bottom",
                                    horizontal: "left",
                                }}
                            >
                                <Box p={3} display="flex" flexDirection="column">
                                    <Box>
                                        <CalendarPicker
                                            fullWidth
                                            label="From date"
                                            outputFormat="yyyy-MM-dd"
                                            date={customDateRange.startDate}
                                            setDate={(newStartDate) =>
                                                setCustomDateRange((prevDateRange) => ({
                                                    ...prevDateRange,
                                                    startDate: newStartDate,
                                                }))
                                            }
                                        />
                                    </Box>
                                    <Box mt={1}>
                                        <CalendarPicker
                                            fullWidth
                                            label="To date"
                                            outputFormat="yyyy-MM-dd"
                                            date={customDateRange.endDate}
                                            setDate={(newEndDate) =>
                                                setCustomDateRange((prevDateRange) => ({
                                                    ...prevDateRange,
                                                    endDate: newEndDate,
                                                }))
                                            }
                                        />
                                    </Box>
                                    <CustomButton
                                        type="button"
                                        color="rose"
                                        onClick={() => {
                                            handleClose();
                                            handleFilterByDateRangeClick();
                                        }}
                                    >
                                        Filter
                                    </CustomButton>
                                </Box>
                            </Popover>
                        </Grid>
                        <Grid item>
                            <ExportSalesDialogButton
                                dealerIds={
                                    dealerId === 0
                                        ? dealers
                                            .filter(
                                                (d) =>
                                                    !selectedCountry ||
                                                    d.country === selectedCountry,
                                            )
                                            .map((d) => d.id)
                                            .join(",")
                                        : dealerId
                                }
                                startDate={customDateRange.startDate}
                                endDate={customDateRange.endDate}
                                disabled={(order?.data || []).length === 0}
                            />
                        </Grid>
                    </Grid>
                </Box>
            </Box>

            {/* CONTENT */}
            <Box mt={2.5} display="flex">
                <Box flex={1}>
                    <OptionsList
                        title="Engagements"
                        options={engagements}
                        selectedCountry={summaryRegion}
                    />
                </Box>
                <Box flex={1}>
                    <OptionsList
                        title="Closing Ratio"
                        options={closingRatio}
                        selectedCountry={summaryRegion}
                    />
                </Box>
                <Box flex={1}>
                    <Box mt={2}>
                        <Typography
                            variant="h6"
                            className={`${classes.customTitle} ${classes.textCenter}`}
                        >
                            Total Orders
                        </Typography>
                        <Box>
                            <Typography variant="h6" className={classes.greenText}>
                                {(Number(totalOrders) || 0).toLocaleString("en-US")}
                            </Typography>
                        </Box>
                    </Box>
                </Box>
                <Box flex={1}>
                    <Box mt={2}>
                        <Typography
                            variant="h6"
                            className={`${classes.customTitle} ${classes.textCenter}`}
                        >
                            Orders Canceled
                        </Typography>
                        <Box>
                            <Typography variant="h6" className={classes.redText}>
                                {(Number(ordersCanceled) || 0).toLocaleString("en-US")}
                            </Typography>
                        </Box>
                    </Box>
                </Box>
                <Box flex={1}>
                    <Box mt={2}>
                        <Typography
                            variant="h6"
                            className={`${classes.customTitle} ${classes.textCenter}`}
                        >
                            Fulfillment Ratio
                        </Typography>
                        <Box>
                            <Typography
                                variant="h6"
                                className={`${classes.normalText} ${classes.centerContent}`}
                            >
                                <ArrowDropUpIcon
                                    color="primary"
                                    style={{ color: "#8ec63f", fontSize: 50 }}
                                />
                                {(Number(fulfillmentRatio) || 0).toFixed(2)}%
                            </Typography>
                        </Box>
                    </Box>
                </Box>
                <Box flex={1}>
                    <Box mt={2} style={{ background: "#8ec63f", color: "#fff" }} p={2}>
                        <Typography
                            variant="h6"
                            className={`${classes.customTitle} ${classes.textCenter}`}
                        >
                            Net Sales:
                        </Typography>
                        <Box textAlign="center">
                            <Typography
                                variant="h6"
                                className={`${classes.normalText} ${classes.centerContent}`}
                                style={{
                                    marginTop: 5,
                                    fontSize: 40,
                                }}
                            >
                                <ArrowDropUpIcon
                                    color="primary"
                                    style={{ color: "#fff", fontSize: 50 }}
                                />
                                {(Number(netSalesPercentage) || 0).toFixed(2)}%
                            </Typography>
                            {formatPriceByRegion(summaryRegion, netSalesAmount)} over last month
                        </Box>
                    </Box>
                </Box>
            </Box>

            <Box mt={2.5} display="flex">
                <Box flex={1}>
                    <OptionsList
                        title="Order Performance"
                        options={orderPerformance}
                        selectedCountry={summaryRegion}
                    />
                </Box>
                <Box flex={1}>
                    <OptionsList
                        title="Sales Performance"
                        options={salesPerformance}
                        selectedCountry={summaryRegion}
                    />
                </Box>
                <Box flex={1}>
                    <Box>
                        <OptionsList
                            title="Shipping Performance"
                            options={shippingPerformance}
                            selectedCountry={summaryRegion}
                        />
                    </Box>
                    <Box>
                        <OptionsList
                            title="Catalog Performance"
                            options={catalogPerformance}
                            selectedCountry={summaryRegion}
                        />
                    </Box>
                </Box>
                <Box flex={1}>
                    <OptionsList title="Tax" options={tax} selectedCountry={summaryRegion} />
                </Box>
                <Box flex={1}>
                    <OptionsList
                        title="Profits"
                        options={profits}
                        selectedCountry={summaryRegion}
                    />
                </Box>
            </Box>

            <Box>
                <Card>
                    <Box p={2}>
                        <Typography variant="h6" className={classes.customTitle}>
                            Report Date: {customDateRange.startDate} - {customDateRange.endDate}
                        </Typography>
                        <Table
                            columns={columns}
                            data={order?.data || []}
                            pageCount={Math.ceil((order?.total || 0) / order?.pageSize)}
                            fetchData={order?.changePagination}
                            manualSortBy={true}
                            manualPagination={true}
                        />
                    </Box>
                </Card>
            </Box>
        </PageLayout>
    );
}
